<script setup lang="ts">
import { computed, ref } from 'vue'

import type { Editor } from '@tiptap/vue-3'

import clsx from 'clsx'

import BaseInputField from '@/components/atoms/BaseInputField.vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'

const props = defineProps<{
    editor: Editor
    isDark: boolean
}>()

const betaStore = useEditorBetaStore()

const isOpenModal = ref(false)
const linkUrl = ref('')
const linkTarget = ref<'_self' | '_blank'>('_blank')
const hasActiveLink = ref(false)

const titleModalLink = computed(() => (hasActiveLink.value ? 'Editar Link' : 'Adicionar Link'))
const confirmModalLink = computed(() => (hasActiveLink.value ? 'Salvar' : 'Adicionar'))

const openModal = () => {
    const linkAttributes = props.editor.getAttributes('link')

    hasActiveLink.value = props.editor.isActive('link')
    linkUrl.value = linkAttributes.href ?? ''
    linkTarget.value = linkAttributes.target === '_self' ? '_self' : '_blank'
    isOpenModal.value = true
}

const closeModal = () => {
    isOpenModal.value = false
}

const removeLink = () => {
    betaStore.getTextEditorCommandChain(props.editor)?.unsetLink().run()
    closeModal()
}

const setLink = () => {
    if (!linkUrl.value) return

    betaStore.getTextEditorCommandChain(props.editor)
        ?.extendMarkRange('link')
        .setLink({
            href: linkUrl.value,
            target: linkTarget.value,
            rel: null,
        })
        .run()

    closeModal()
}
</script>

<template>
    <button
        type="button"
        @click="openModal"
        :class="clsx(
            'action-link',
            editor.isActive('link') ? 'is-active' : 'is-deactive',
            isDark ? 'dark' : 'light'
        )"
    >
        <span class="sr-only">Link</span>

        <svg
            class="h-6 w-[25px]"
            stroke="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 25 24"
            fill="none"
        >
            <path
                d="M8.18188 10.8286L6.32066 12.6898C5.62555 13.3849 5.22297 14.3279 5.22506 15.3167C5.22715 16.3055 5.61115 17.25 6.33178 17.9482C7.02988 18.6688 7.97466 19.0528 8.9633 19.0549C9.97448 19.057 10.8951 18.677 11.5902 17.9819L13.4514 16.1206M15.8285 13.7885L17.6897 11.9273C18.3849 11.2322 18.7874 10.2892 18.7853 9.30045C18.7833 8.31166 18.3993 7.36708 17.6786 6.66893C16.9806 5.97088 16.036 5.58686 15.0472 5.58477C14.0584 5.58268 13.1154 5.96269 12.4202 6.65782L10.559 8.51903M9.18305 15.0815L14.7667 9.49782"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </svg>
    </button>

    <base-modal
        :open-watch="isOpenModal"
        @modal-closed="closeModal"
        :title="titleModalLink"
        size="custom"
        height="h-[20rem]"
        width="w-[30rem]"
        rounded="2xl"
        extra-class="beta-text-link-modal text-link-modal"
    >
        <template #body>
            <div class="beta-text-link-modal__body" data-keep-beta-text-focus>
                <div class="beta-text-link-modal__fields">
                    <base-input-field label="Link" id="beta-text-link-url">
                        <input
                            id="beta-text-link-url"
                            v-model="linkUrl"
                            type="text"
                            name="link"
                            placeholder="https://exemplo.com.br"
                            @keydown.enter.prevent="setLink"
                        />
                    </base-input-field>

                    <label class="beta-text-link-modal__field">
                        <span>Abrir em</span>

                        <select v-model="linkTarget">
                            <option value="_self">Mesma aba</option>
                            <option value="_blank">Nova aba</option>
                        </select>
                    </label>
                </div>

                <div class="beta-text-link-modal__footer">
                    <button
                        v-if="hasActiveLink"
                        type="button"
                        class="beta-text-link-modal__remove"
                        @click="removeLink"
                    >
                        Remover
                    </button>

                    <div class="beta-text-link-modal__actions">
                        <button
                            type="button"
                            class="beta-text-link-modal__cancel"
                            @click="closeModal"
                        >
                            Cancelar
                        </button>

                        <button
                            type="button"
                            class="beta-text-link-modal__confirm"
                            @click="setLink"
                        >
                            {{ confirmModalLink }}
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </base-modal>
</template>

<style scoped>
.action-link.light svg {
    @apply stroke-[#060606];
}

.action-link.dark svg {
    @apply stroke-[#FAFAFA];
}

.action-link.is-deactive svg {
    @apply opacity-60;
}

.action-link.is-active svg {
    @apply opacity-100;
}

.beta-text-link-modal__body {
    @apply flex h-full flex-col justify-between;
}

.beta-text-link-modal__fields {
    @apply flex flex-col gap-4;
}

.beta-text-link-modal__field {
    @apply flex flex-col gap-2 text-sm font-semibold text-[#14213D];
}

.beta-text-link-modal__field select {
    @apply h-11 rounded-xl border border-[#DCE5F2] bg-white px-3 text-sm font-medium text-[#14213D] outline-none transition-colors duration-200 focus:border-[var(--main-color)];
}

.beta-text-link-modal__footer {
    @apply mt-5 flex items-center justify-between gap-3;
}

.beta-text-link-modal__actions {
    @apply ml-auto flex items-center gap-2;
}

.beta-text-link-modal__remove {
    @apply rounded-full bg-transparent px-4 py-2 text-sm font-medium text-red-500 outline-none transition-colors duration-200 hover:text-gray-500;
}

.beta-text-link-modal__cancel {
    @apply rounded-full bg-transparent px-4 py-2 text-sm font-medium text-[#9AA6B7] outline-none transition-colors duration-200 hover:text-gray-500;
}

.beta-text-link-modal__confirm {
    @apply rounded-full bg-[var(--main-color)] px-4 py-2 text-sm font-medium text-white outline-none transition-colors duration-200 hover:brightness-95;
}
</style>
